import React from 'react';
import * as S from './styles';
import { IconDocumentSignature, Checkbox, Button } from '@veneer/core';
import { useTranslationHelper } from 'src/contexts/dependencyManager/utils';

interface TermsofServiceComponentProps {
  onBack: () => void;
  onConfirm?: () => void;
}

const TermsofServiceComponent = ({
  onBack,
  onConfirm
}: TermsofServiceComponentProps): JSX.Element => {
  const [agreeToTerms, setAgreeToTerms] = React.useState(false);
  const [understandChecked, setUnderstandChecked] = React.useState(false);

  const getText = useTranslationHelper();

  const HP_TERMS_URL = 'https://www.hp.com/us-en/all-in-plan/terms.html';

  const CANCEL_CONTACT_INFO =
    "at MyAccount or 1-888-447-0148. Individual services can't be cancelled except for paper add on.";

  const createTermTitle = (key: string, defaultText: string) => (
    <b>{getText(key, defaultText)}</b>
  );

  return (
    <S.TermsServiceContainer>
      <S.TermsContainer>
        <IconDocumentSignature size={32} />
        <S.Heading>
          {getText('termsOfService.title', 'Terms of service')}
        </S.Heading>
        <S.TermsText>
          {createTermTitle('termsOfService.term1.title', 'Automatic Renewal. ')}
          {getText(
            'termsOfService.term1.description',
            'Your All-In Plan monthly payments (plus taxes or additional pages) will be automatically charged at the end of each billing cycle and automatically renew and continue unless you cancel. Printer and cartridges remain the property of HP and must be returned to HP upon upgrade, replacement or cancellation.'
          )}
        </S.TermsText>
        <S.TermsText>
          {createTermTitle('termsOfService.term2.title', 'Amount due today')}
          is $0
        </S.TermsText>
        <S.TermsText>
          {createTermTitle('termsOfService.term3.title', 'First payment')}
          is Aug 16
        </S.TermsText>
        <S.TermsText>
          {createTermTitle(
            'termsOfService.term4.title',
            'Total amount paid in first 36 months'
          )}
          {getText(
            'termsOfService.term4.description',
            'will be $593.28, plus any taxes and additional page set fees.'
          )}
        </S.TermsText>
        <S.TermsText>
          {createTermTitle(
            'termsOfService.term5.title',
            'Change or cancel plan anytime'
          )}
          {getText('termsOfService.term5.description1', 'at')}{' '}
          <S.RedirectLink
            href={HP_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {getText('termsOfService.term5.description2', 'MyAccount')}
          </S.RedirectLink>{' '}
          {getText(
            'termsOfService.term5.description3',
            "or 1-888-447-0148. Individual services can't be cancelled except for paper add on."
          )}
        </S.TermsText>
        <S.TermsText>
          {createTermTitle(
            'termsOfService.term6.title',
            'Cancellation within the first 30 days'
          )}
          {getText('termsOfService.term6.description', CANCEL_CONTACT_INFO)}
        </S.TermsText>
        <S.TermsText>
          {createTermTitle(
            'termsOfService.term7.title',
            'Cancellation after the first 30 days: '
          )}
          {getText('termsOfService.term7.description', CANCEL_CONTACT_INFO)}
        </S.TermsText>
        <S.TermsText>
          {createTermTitle(
            'termsOfService.term8.title',
            'Wi-Fi® or Ethernet connection required. '
          )}
          {getText('termsOfService.term8.description', CANCEL_CONTACT_INFO)}
        </S.TermsText>
        <S.TermsText>
          {createTermTitle(
            'termsOfService.term9.title',
            'Other important terms apply.'
          )}
          {getText('termsOfService.term9.description1', 'See')}{' '}
          <S.RedirectLink
            href={HP_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {getText(
              'termsOfService.term9.description2',
              'HP All-In Plan Terms of Service'
            )}
          </S.RedirectLink>{' '}
          {getText(
            'termsOfService.term9.description3',
            'for additional information on early termination, support, replacement service and other important terms.'
          )}
        </S.TermsText>
        <S.TermsText>July 16, 2025</S.TermsText>
        <S.Separator />
        <S.CheckboxContainer>
          <Checkbox
            id="input-checkbox"
            label={
              <>
                {getText(
                  'termsOfService.I-agree1',
                  'I agree to the above disclosure and full'
                )}{' '}
                <S.RedirectLink
                  href={HP_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getText(
                    'termsOfService.I-agree2',
                    'HP All-In Plan Terms of Service.'
                  )}
                </S.RedirectLink>
              </>
            }
            checked={agreeToTerms}
            onChange={(e) => setAgreeToTerms(e.target.checked)}
          />
        </S.CheckboxContainer>
      </S.TermsContainer>
      <S.UnderstandBlock>
        <S.CheckboxContainer>
          <Checkbox
            id="checkbox"
            label={getText(
              'termsOfService.I-understand',
              'I understand and agree that I will return my current HP All-In Plan printer to HP once I receive the new upgrade printer. HP will provide a free FedEx shipping label and box if needed.'
            )}
            checked={understandChecked}
            onChange={(e) => setUnderstandChecked(e.target.checked)}
          />
        </S.CheckboxContainer>
      </S.UnderstandBlock>

      <S.ActionButtons>
        <S.ContinueButton
          disabled={!agreeToTerms || !understandChecked}
          onClick={() => onConfirm()}
        >
          Confirm Printer Upgrade
        </S.ContinueButton>
        <Button
          appearance="secondary"
          onClick={() => onBack()}
          data-testid="upgradeReviewBackButton"
        >
          Back
        </Button>
      </S.ActionButtons>
    </S.TermsServiceContainer>
  );
};

export default TermsofServiceComponent;
